import * as Sentry from '@sentry/react';

/**
 * Capture a message to Sentry with proper grouping and unique occurrence tracking
 * @param message - The message to capture
 * @param options - Additional Sentry options
 */
export interface SentryMessageOptions {
  level?: 'error' | 'warning' | 'info' | 'debug';
  fingerprint: string[]; // Base fingerprint for grouping
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
}

const buildUniqueId = (): string => Math.random().toString(36).substring(2, 15);
const buildOccurrenceTime = (timestamp: number) => new Date(timestamp).toISOString().split('T')[0];

export const captureGroupedMessage = (message: string, options: SentryMessageOptions): void => {
  const timestamp = Date.now();
  const uniqueId = buildUniqueId();

  // Make the message unique to avoid Sentry's deduplication while keeping it grouped by fingerprint
  const uniqueMessage = `${message} [${new Date(timestamp).toISOString()}]`;

  Sentry.captureMessage(uniqueMessage, {
    level: options.level || 'error',
    fingerprint: options.fingerprint,
    extra: {
      ...options.extra,
      // Add unique data to prevent deduplication while maintaining grouping
      original_message: message, // Keep original message for reference
      occurrence_timestamp: timestamp,
      occurrence_id: uniqueId,
    },
    tags: {
      ...options.tags,
      occurrence_time: buildOccurrenceTime(timestamp), // Group by date
    },
    contexts: {
      ...options.contexts,
      occurrence_metadata: {
        timestamp: new Date(timestamp).toISOString(),
        unique_id: uniqueId,
        session_id: 'session_' + uniqueId,
      },
    },
  });
};

/**
 * Capture an exception to Sentry with proper grouping and unique occurrence tracking
 */
export const captureGroupedException = (error: Error, options: Omit<SentryMessageOptions, 'level'>): void => {
  const timestamp = Date.now();
  const uniqueId = buildUniqueId();

  Sentry.captureException(error, {
    fingerprint: options.fingerprint, // Use provided fingerprint for grouping
    extra: {
      ...options.extra,
      // Add unique data to prevent deduplication while maintaining grouping
      occurrence_timestamp: timestamp,
      occurrence_id: uniqueId,
      error_stack: error.stack,
    },
    tags: {
      ...options.tags,
      error_type: error.name,
      occurrence_time: buildOccurrenceTime(timestamp), // Group by date
    },
    contexts: {
      ...options.contexts,
      error_details: {
        message: error.message,
        name: error.name,
        timestamp: new Date(timestamp).toISOString(),
        unique_occurrence: uniqueId,
      },
    },
  });
};
